import { HGIntervalType } from "./types";

interface IntervalTypeInfo {
  plural: string;
  singular: string;
  parentType?: HGIntervalType;
  childrenTypes: HGIntervalType[];
  // Which of childrenTypes to drill into by default (HGStack.push(),
  // GroupRecords()). Defaults to childrenTypes[0] when omitted. Needed once
  // a type has more than one child so childrenTypes can be ordered
  // coarse->fine for display without silently rewiring navigation.
  defaultChildType?: HGIntervalType;
  // Largest |n| the engine can map, when the type is bounded. Only the top
  // type needs it: every other level is bounded by its parent instead.
  maxN?: bigint;
}

interface IntervalTypesRegistry {
  // Unknown types are a normal runtime case (a persisted stack, an interval
  // coming from the API), so lookups must be checked rather than trusted.
  [it: HGIntervalType]: IntervalTypeInfo | undefined;
}

const INTERVAL_TYPES: IntervalTypesRegistry = {
  // Top of the hierarchy: no parentType, so a stack sitting on "byr" has no
  // parent level to display or navigate to. It is also finite — the engine
  // (its/byr.cpp, MAX_S) can't map a byr past 292 to seconds.
  byr: {
    plural: "billion years",
    singular: "billion years",
    childrenTypes: ["myr", "byr_h", "byr_t", "byr_q"],
    defaultChildType: "myr",
    maxN: 14n,
  },
  byr_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "byr",
    childrenTypes: ["myr"],
  },
  byr_t: {
    plural: "thirds",
    singular: "third",
    parentType: "byr",
    childrenTypes: ["myr"],
  },
  byr_h: {
    plural: "halves",
    singular: "half",
    parentType: "byr",
    childrenTypes: ["myr"],
  },
  myr: {
    plural: "million years",
    singular: "million years",
    parentType: "byr",
    childrenTypes: ["kyr", "myr_h", "myr_t", "myr_q"],
    defaultChildType: "kyr",
  },
  myr_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "myr",
    childrenTypes: ["kyr"],
  },
  myr_t: {
    plural: "thirds",
    singular: "third",
    parentType: "myr",
    childrenTypes: ["kyr"],
  },
  myr_h: {
    plural: "halves",
    singular: "half",
    parentType: "myr",
    childrenTypes: ["kyr"],
  },
  kyr: {
    plural: "thousand years",
    singular: "thousand years",
    parentType: "myr",
    childrenTypes: ["c", "kyr_h", "kyr_t", "kyr_q"],
    defaultChildType: "c",
  },
  kyr_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "kyr",
    childrenTypes: ["dec"],
  },
  kyr_t: {
    plural: "thirds",
    singular: "third",
    parentType: "kyr",
    childrenTypes: ["dec"],
  },
  kyr_h: {
    plural: "halves",
    singular: "half",
    parentType: "kyr",
    childrenTypes: ["dec"],
  },
  c: {
    plural: "centuries",
    singular: "century",
    parentType: "kyr",
    childrenTypes: ["y", "dec", "c_h", "c_t", "c_q"],
    defaultChildType: "y",
  },
  c_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "c",
    childrenTypes: ["y"],
  },
  c_t: {
    plural: "thirds",
    singular: "third",
    parentType: "c",
    childrenTypes: ["y"],
  },
  c_h: {
    plural: "halves",
    singular: "half",
    parentType: "c",
    childrenTypes: ["y"],
  },
  dec: {
    plural: "decades",
    singular: "decade",
    parentType: "c",
    childrenTypes: ["y"],
  },
  y: {
    plural: "years",
    singular: "year",
    parentType: "dec",
    childrenTypes: ["m", "w", "y_h", "y_t", "y_q"],
    defaultChildType: "m",
  },
  y_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "y",
    childrenTypes: ["m"],
  },
  y_t: {
    plural: "thirds",
    singular: "third",
    parentType: "y",
    childrenTypes: ["m"],
  },
  y_h: {
    plural: "halves",
    singular: "half",
    parentType: "y",
    childrenTypes: ["m"],
  },
  m: {
    plural: "months",
    singular: "month",
    parentType: "y",
    childrenTypes: ["d", "w", "m_h", "m_t"],
    defaultChildType: "d",
  },
  m_t: {
    plural: "thirds",
    singular: "third",
    parentType: "m",
    childrenTypes: ["d"],
  },
  m_h: {
    plural: "halves",
    singular: "half",
    parentType: "m",
    childrenTypes: ["d"],
  },
  w: {
    plural: "weeks",
    singular: "week",
    parentType: "m",
    childrenTypes: ["d"],
  },
  d: {
    plural: "days",
    singular: "day",
    parentType: "m",
    childrenTypes: ["h", "d_h", "d_t", "d_q"],
    defaultChildType: "h",
  },
  d_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "d",
    childrenTypes: ["h"],
  },
  d_t: {
    plural: "thirds",
    singular: "third",
    parentType: "d",
    childrenTypes: ["h"],
  },
  d_h: {
    plural: "halves",
    singular: "half",
    parentType: "d",
    childrenTypes: ["h"],
  },
  h: {
    plural: "hours",
    singular: "hour",
    parentType: "d",
    childrenTypes: ["min", "h_h", "h_t", "h_q"],
    defaultChildType: "min",
  },
  h_q: {
    plural: "quarters",
    singular: "quarter",
    parentType: "h",
    childrenTypes: ["min"],
  },
  h_t: {
    plural: "thirds",
    singular: "third",
    parentType: "h",
    childrenTypes: ["min"],
  },
  h_h: {
    plural: "halves",
    singular: "half",
    parentType: "h",
    childrenTypes: ["min"],
  },
  min: {
    plural: "minutes",
    singular: "minute",
    parentType: "h",
    childrenTypes: ["s"],
  },
  // Bottom of the hierarchy: no children, so it can't be drilled into.
  s: {
    plural: "seconds",
    singular: "second",
    parentType: "min",
    childrenTypes: [],
  },
  // ms: {
  //   plural: "milliseconds",
  //   childrenTypes: ["us"],
  // },
  // us: {
  //   plural: "microseconds",
  //   childrenTypes: [],
  // },
};

// The accessors below take an optional type and answer "nothing" for types
// that are unknown or missing: the top type has no parent and the bottom type
// has no children, and callers walking the hierarchy hit both edges.

export function getChildrenTypes(
  intervalType?: HGIntervalType,
): HGIntervalType[] {
  return (intervalType && INTERVAL_TYPES[intervalType]?.childrenTypes) || [];
}

export function getPlural(intervalType: HGIntervalType) {
  return INTERVAL_TYPES[intervalType]?.plural ?? intervalType;
}

export function getSingular(intervalType: HGIntervalType) {
  return INTERVAL_TYPES[intervalType]?.singular ?? intervalType;
}

export function childTypeAllowed(
  intervalType: HGIntervalType | undefined,
  childIntervalType: HGIntervalType,
) {
  return getChildrenTypes(intervalType).includes(childIntervalType);
}

export function getChildType(
  intervalType?: HGIntervalType,
): HGIntervalType | undefined {
  const info = intervalType ? INTERVAL_TYPES[intervalType] : undefined;
  if (!info) return undefined;
  return info.defaultChildType ?? info.childrenTypes[0];
}

export function getMaxN(intervalType: HGIntervalType): bigint | undefined {
  return INTERVAL_TYPES[intervalType]?.maxN;
}

export function getParentType(
  intervalType?: HGIntervalType,
): HGIntervalType | undefined {
  return intervalType ? INTERVAL_TYPES[intervalType]?.parentType : undefined;
}

export default INTERVAL_TYPES;
